import React, { Component } from 'react'
import { Form, HelpBlock, FormGroup, Button } from 'react-bootstrap'
import '../App.css'
import { embedComponent } from '../embed'
import Field from '../components/Field'
import CurrencyField from '../components/CurrencyField'
import formatCurrency from '../utils/formatters/formatCurrency'
import numberWithLabelChoices from '../utils/forms/numberWithLabelChoices'
import CalculatorResults from '../components/CalculatorResults/index'
import CalculatorResult from '../components/CalculatorResult/index'
import Calculator from '../components/Calculator/index'
import PercentageField from '../components/PercentageField/index'
import CalculatorButtons from '../components/CalculatorButtons/index'
import { minMaxValidator } from '../utils/validators/index'
import formatPercentage from '../utils/formatters/formatPercentage'
import { DEFAULT_MAX_DOLLAR_VALUE } from '../utils/validators'

const popoverMessages = {
  mortgageAmount: {
    title: 'Mortgage amount',
    body: [
      'This is the amount you intend to borrow from the bank.',
      'Typically, it is about 80% of the value of the house you intend to buy, the remaining 20% coming in the form of a down payment you have saved up.',
    ],
  },
  mortgageTerm: {
    title: 'Term (years)',
    body: [
      'This is the length of the loan.',
      'Typically, it is 30 years, although it can also be 10 or 15. Loans for periods less than 10 years are more complicated and often involve variable interest rates. <a href="/#/articles/163" target="_blank">Click here to learn about different loan types</a>.',
    ],
  },
  maxMortgageMortgageTerm: {
    title: 'Term (years)',
    body: [
      'This is the length of the loan.',
      'Typically, it is 30 years, although it can also be 10 or 15. Loans for shorter periods are more complicated and often involve variable interest rates. <a href="/#/articles/163" target="_blank">Click here to learn about different loan types</a>.',
    ],
  },
  mortgageInterestRate: {
    title: 'Interest rate (%)',
    body: [
      'This is the rate of interest you pay on the loan.',
      'Typically, it is fixed for the length of the loan. It does not include fees or any additional costs charged by the lender.',
    ],
  },
  monthlyPayment: {
    title: 'Monthly payment',
    body: [
      'This is the monthly mortgage payment you will have as a result of the loan.',
      'It does not include taxes, insurance, maintenance or other costs of owning a home. Click here to learn more about the total costs of home ownership.',
    ],
  },
  mortgageCost: {
    title: 'Total mortgage cost',
    body: [
      'This is the total amount amount you will end up paying to the bank.',
      'It is the total of your payments over the life of the loan and includes the interest you must pay on the amount borrowed.',
    ],
  },
  monthlyPaymentInput: {
    title: 'Maximum monthly payment',
    body: [
      'This is the amount you can afford to pay each month.',
      'Typically, it is not more than 25% of your gross monthly income.',
    ],
  },
  youCouldBorrow: {
    title: 'You could borrow',
    body: [
      'This is the amount you could borrow given what you can afford in monthly payments.',
      'Typically, you would also need a down payment and have to meet other criteria to qualify for a mortgage.',
    ],
  },
}

const MORTGAGE_TERM_5_1_ARM = '5/1 ARM'

// Create select input options for dropdown.
const mortgageTermYearValues = [10, 15, 20, 25, 30]
const mortgageTermOptions = numberWithLabelChoices(mortgageTermYearValues, ' years')
mortgageTermOptions.unshift({ label: MORTGAGE_TERM_5_1_ARM, value: MORTGAGE_TERM_5_1_ARM })

class MortgageCalculator extends Component {
  constructor (props) {
    super(props)

    this.state = MortgageCalculator.getInitialState()

    this.clearState = this.clearState.bind(this)
    this.onMortgageValidation = this.onMortgageValidation.bind(this)
    this.onMaxMortgageValidation = this.onMaxMortgageValidation.bind(this)
  }

  static getInitialState() {
    return {
      mortgageAmount: null,
      mortgageTerm: 30,
      mortgageInterestRate: null,
      monthlyPayment: null,
      mortgageCost: null,
      maxMortgageMonthlyPayment: null,
      maxMortgageMortgageTerm: 30,
      maxMortgageMortgageInterestRate: null,
      mortgageHasError: {},
      maxMortgageHasError: {},
      activeTab: 1
    }
  }

  static normalizeMortgageTerm(mortgageTerm) {
    return mortgageTerm === MORTGAGE_TERM_5_1_ARM ? 30 : mortgageTerm
  }

  handleChange (value, fieldName) {
    this.setState({ [fieldName]: value })
  }

  onValidation(name, error, stateKey) {
    if (typeof error !== 'undefined') {
      // Has errors
      if (!this.state[stateKey][name]) {
        this.setState({ [stateKey]: Object.assign({}, this.state[stateKey], { [name]: error }) })
      }
    } else {
      // No errors
      if (this.state[stateKey][name]) {
        const newErrors = Object.assign({}, this.state[stateKey])
        delete newErrors[name]
        this.setState({ [stateKey]: newErrors })
      }
    }
  }

  onMortgageValidation(name, error) {
    this.onValidation(name, error, 'mortgageHasError')
  }

  onMaxMortgageValidation(name, error) {
    this.onValidation(name, error, 'maxMortgageHasError')
  }

  clearState () {
    this.setState(MortgageCalculator.getInitialState())
  }

  calculateMonthlyPayment () {
    const { mortgageAmount, mortgageTerm, mortgageInterestRate } = this.state

    const normalizedMortgageTerm = MortgageCalculator.normalizeMortgageTerm(mortgageTerm)

    const numPayments = normalizedMortgageTerm * 12
    const interestRate = mortgageInterestRate / 100 / 12
    const exponentQuantity = Math.pow(1 + interestRate, numPayments)
    const numerator = interestRate * exponentQuantity
    const denominator = exponentQuantity - 1

    return denominator > 0 ? mortgageAmount * (numerator / denominator) : 0
  }

  calculateMaxMortgage () {
    const {
      maxMortgageMortgageTerm,
      maxMortgageMortgageInterestRate,
      maxMortgageMonthlyPayment,
    } = this.state

    const normalizedMortgageTerm = MortgageCalculator.normalizeMortgageTerm(maxMortgageMortgageTerm)

    const numPayments = normalizedMortgageTerm * 12
    const interestRate = maxMortgageMortgageInterestRate / 100 / 12
    const exponentQuantity = Math.pow(1 + interestRate, numPayments)
    const numerator = exponentQuantity - 1
    const denominator = interestRate * exponentQuantity

    return denominator > 0 ? maxMortgageMonthlyPayment * (numerator / denominator) : 0
  }

  handleTabSelect(activeTab) {
    this.setState({ activeTab })
  }

  render () {
    const {
      mortgageAmount,
      mortgageTerm,
      maxMortgageMortgageTerm,
      mortgageInterestRate,
      maxMortgageMortgageInterestRate,
      maxMortgageMonthlyPayment,
      activeTab
    } = this.state

    let normalizedMortgageTerm = MortgageCalculator.normalizeMortgageTerm(mortgageTerm)
    let normalizedMaxMortgageTerm = MortgageCalculator.normalizeMortgageTerm(maxMortgageMortgageTerm)

    const strippedMaxMonthlyPayments = maxMortgageMonthlyPayment
    const numPayments = normalizedMortgageTerm * 12
    const maxMortgageMumPayments = normalizedMaxMortgageTerm * 12
    const calculatedMonthlyPayment = this.calculateMonthlyPayment()
    const calculatedMaxMortgage = this.calculateMaxMortgage()
    const calculatedTotalCost = numPayments * calculatedMonthlyPayment
    const calculatedMaxMortgageTotalCost = maxMortgageMumPayments * strippedMaxMonthlyPayments

    return (
      <Calculator title="Mortgage Calculator" className="mortgage-calculator">
        <div className="tab-buttons">
          <Button className={activeTab === 1 ? 'active' : ''} onClick={() => this.handleTabSelect(1)}>Monthly Payment</Button>
          <Button className={activeTab === 2 ? 'active' : ''} onClick={() => this.handleTabSelect(2)}>Maximum Mortgage</Button>
        </div>
        <div className="calculator-body">
          {activeTab === 1 ? (
            <div>
              <Form>
                <CurrencyField
                  name="mortgageAmount"
                  label="Mortgage Amount"
                  value={mortgageAmount}
                  onChange={(e, value) => this.handleChange(value, 'mortgageAmount') }
                  popover={popoverMessages.mortgageAmount}
                  validator={minMaxValidator(1, DEFAULT_MAX_DOLLAR_VALUE, formatCurrency)}
                  onValidation={this.onMortgageValidation}
                  />

                <Field
                  type="select"
                  name="mortgageTerm"
                  label="Term (years)"
                  value={mortgageTerm}
                  choices={mortgageTermOptions}
                  onChange={(e, value) => this.handleChange(value, 'mortgageTerm') }
                  popover={popoverMessages.mortgageTerm}
                  />

                <PercentageField
                  name="mortgageInterestRate"
                  label="Interest Rate (%)"
                  value={mortgageInterestRate}
                  onChange={(e, value) => this.handleChange(value, 'mortgageInterestRate') }
                  popover={popoverMessages.mortgageInterestRate}
                  validator={minMaxValidator(0.001, 30, formatPercentage)}
                  onValidation={this.onMortgageValidation}
                  />
              </Form>
              <CalculatorResults>
                <CalculatorResult
                  label={'Monthly Payment'}
                  value={formatCurrency(Math.ceil((Object.keys(this.state.mortgageHasError).length === 0 && calculatedMonthlyPayment) || 0))}
                  popover={popoverMessages.monthlyPayment}
                  />
                <CalculatorResult
                  label={'Total Mortgage Cost'}
                  value={formatCurrency(Math.ceil((Object.keys(this.state.mortgageHasError).length === 0 && calculatedTotalCost) || 0))}
                  popover={popoverMessages.mortgageCost}
                  />
              </CalculatorResults>
              {mortgageTerm === MORTGAGE_TERM_5_1_ARM && (
                <FormGroup validationState="error">
                  <HelpBlock>
                    The	monthly payment shown may increase after year five.
                  </HelpBlock>
                </FormGroup>
              )}
              <CalculatorButtons onClear={this.clearState} />
            </div>
          ) : (
            <div>
              <Form>
                <CurrencyField
                  name="maxMortgageMonthlyPayment"
                  label="Maximum Monthly Payment"
                  value={maxMortgageMonthlyPayment}
                  onChange={(e, value) => this.handleChange(value, 'maxMortgageMonthlyPayment') }
                  popover={popoverMessages.monthlyPaymentInput}
                  validator={minMaxValidator(1, DEFAULT_MAX_DOLLAR_VALUE, formatCurrency)}
                  onValidation={this.onMaxMortgageValidation}
                  />
                <Field
                  type="select"
                  name="maxMortgageMortgageTerm"
                  label="Term (years)"
                  value={maxMortgageMortgageTerm}
                  choices={mortgageTermOptions}
                  onChange={(e, value) => this.handleChange(value, 'maxMortgageMortgageTerm') }
                  popover={popoverMessages.maxMortgageMortgageTerm}
                  />

                <PercentageField
                  name="maxMortgageMortgageInterestRate"
                  label="Interest Rate (%)"
                  value={maxMortgageMortgageInterestRate}
                  onChange={(e, value) => this.handleChange(value, 'maxMortgageMortgageInterestRate') }
                  popover={popoverMessages.mortgageInterestRate}
                  validator={minMaxValidator(0.001, 30, formatPercentage)}
                  onValidation={this.onMaxMortgageValidation}
                  />
              </Form>
              <CalculatorResults>
                <CalculatorResult
                  label={'You could borrow'}
                  value={formatCurrency(Math.ceil((Object.keys(this.state.maxMortgageHasError).length === 0 && calculatedMaxMortgage) || 0))}
                  popover={popoverMessages.youCouldBorrow}
                  />
                <CalculatorResult
                  label={'Total Mortgage Cost'}
                  value={formatCurrency(Math.ceil((Object.keys(this.state.maxMortgageHasError).length === 0 && calculatedMaxMortgageTotalCost) || 0))}
                  popover={popoverMessages.mortgageCost}
                  />
              </CalculatorResults>
              {maxMortgageMortgageTerm === MORTGAGE_TERM_5_1_ARM && (
                <FormGroup validationState="error">
                  <HelpBlock>
                    The	monthly payment shown may increase after year five.
                  </HelpBlock>
                </FormGroup>
              )}
              <CalculatorButtons onClear={this.clearState} />
            </div>
          )}
        </div>
      </Calculator>
    )
  }
}

embedComponent(MortgageCalculator, 'tgm-mortgage-calc', 'tgm-mortgage-calc-embed')

export default MortgageCalculator



// WEBPACK FOOTER //
// ./src/calculators/MortgageCalculator.js